class PhysicsShapeInner {
    _wasmInstance;
    _ptr;
    _referenceCount;
    constructor(wasmInstance, ptr) {
        this._wasmInstance = wasmInstance;
        this._ptr = ptr;
        this._referenceCount = 0;
    }
    dispose() {
        if (this._referenceCount > 0) {
            throw new Error("Cannot dispose shape while it still has references");
        }
        if (this._ptr === 0) {
            return;
        }
        // this operation is thread-safe because the physics shape is not belong to any physics world
        this._wasmInstance.deref()?.destroyShape(this._ptr);
        this._ptr = 0;
    }
    get ptr() {
        return this._ptr;
    }
    addReference() {
        this._referenceCount += 1;
    }
    removeReference() {
        this._referenceCount -= 1;
    }
}
function PhysicsShapeFinalizer(inner) {
    inner.dispose();
}
const PhysicsShapeRegistryMap = new WeakMap();
/**
 * Base class for all bullet physics shapes
 */
export class PhysicsShape {
    runtime;
    _inner;
    constructor(runtime, ptr) {
        this.runtime = runtime;
        this._inner = new PhysicsShapeInner(new WeakRef(runtime.wasmInstance), ptr);
        let registry = PhysicsShapeRegistryMap.get(runtime.wasmInstance);
        if (registry === undefined) {
            registry = new FinalizationRegistry(PhysicsShapeFinalizer);
            PhysicsShapeRegistryMap.set(runtime.wasmInstance, registry);
        }
        registry.register(this, this._inner, this);
    }
    dispose() {
        if (this._inner.ptr === 0) {
            return;
        }
        this._inner.dispose();
        const registry = PhysicsShapeRegistryMap.get(this.runtime.wasmInstance);
        registry?.unregister(this);
    }
    /**
     * @internal
     */
    get ptr() {
        return this._inner.ptr;
    }
    /**
     * @internal
     */
    addReference() {
        this._inner.addReference();
    }
    /**
     * @internal
     */
    removeReference() {
        this._inner.removeReference();
    }
}
/**
 * Box shape
 */
export class PhysicsBoxShape extends PhysicsShape {
    /**
     * Creates a new box shape
     * @param runtime physics runtime
     * @param size half extents of the box shape
     */
    constructor(runtime, size) {
        const ptr = runtime.wasmInstance.createBoxShape(size.x, size.y, size.z);
        super(runtime, ptr);
    }
}
/**
 * Sphere shape
 */
export class PhysicsSphereShape extends PhysicsShape {
    /**
     * Creates a new sphere shape
     * @param runtime physics runtime
     * @param radius radius of the sphere shape
     */
    constructor(runtime, radius) {
        const ptr = runtime.wasmInstance.createSphereShape(radius);
        super(runtime, ptr);
    }
}
/**
 * Cylinder shape
 */
export class PhysicsCapsuleShape extends PhysicsShape {
    /**
     * Creates a new capsule shape
     * @param runtime physics runtime
     * @param radius radius of the capsule shape
     * @param height height of the capsule shape
     */
    constructor(runtime, radius, height) {
        const ptr = runtime.wasmInstance.createCapsuleShape(radius, height);
        super(runtime, ptr);
    }
}
/**
 * Static plane shape
 */
export class PhysicsStaticPlaneShape extends PhysicsShape {
    /**
     * Creates a new static plane shape
     * @param runtime physics runtime
     * @param normal normal of the plane shape
     * @param planeConstant constant of the plane shape
     */
    constructor(runtime, normal, planeConstant) {
        const ptr = runtime.wasmInstance.createStaticPlaneShape(normal.x, normal.y, normal.z, planeConstant);
        super(runtime, ptr);
    }
}
